<script setup lang="ts">
import type { TopicMeta } from '../../types/topic-meta'
import { imageUrl } from '../utils/url.ts'

const isCreateOpen = ref(false)
const creatingTopic = ref(false)
const topicTitle = ref('')
const topicDescription = ref('')
const topicName = ref('')
const topicHidden = ref(false)
const topicPreviewFile = ref<File | null>(null)
const topicPreviewObjectUrl = ref('')
const topicPreviewInputRef = ref<HTMLInputElement | null>(null)
const topicPreviewDropZoneRef = ref<HTMLElement | null>(null)

const router = useRouter()
const toast = useToast()
const { loggedIn } = useUserSession()

const { data: topics, refresh } = await useFetch<TopicMeta[]>('/api/topics', {
  default: () => []
})

const visibleTopics = computed(() => loggedIn.value ? (topics.value || []) : (topics.value || []).filter(topic => !topic.hidden))

const topicBackgroundImagePath = ref<string | null>(null)

watch(visibleTopics, (value) => {
  const previewPaths = (value || [])
    .map(topic => topic.previewImagePath)
    .filter((path): path is string => !!path)

  if (!previewPaths.length) {
    topicBackgroundImagePath.value = null
    return
  }

  const randomIndex = Math.floor(Math.random() * previewPaths.length)
  topicBackgroundImagePath.value = previewPaths[randomIndex] ?? null
}, { immediate: true })

watch(topicTitle, (value) => {
  topicName.value = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
})

watch(topicPreviewFile, (file, previous) => {
  if (topicPreviewObjectUrl.value) {
    URL.revokeObjectURL(topicPreviewObjectUrl.value)
    topicPreviewObjectUrl.value = ''
  }

  if (file) {
    topicPreviewObjectUrl.value = URL.createObjectURL(file)
  }

  if (previous && previous !== file) {
    // no-op, kept for clarity to avoid stale object URLs
  }
})

onBeforeUnmount(() => {
  if (topicPreviewObjectUrl.value) {
    URL.revokeObjectURL(topicPreviewObjectUrl.value)
  }
})

function openPreviewPicker() {
  topicPreviewInputRef.value?.click()
}

function clearPreviewSelection() {
  topicPreviewFile.value = null
  if (topicPreviewInputRef.value) {
    topicPreviewInputRef.value.value = ''
  }
}

function setPreviewFile(file?: File | null) {
  if (!file || !file.type.startsWith('image/')) {
    return
  }

  topicPreviewFile.value = file
}

function onPreviewFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  setPreviewFile(file)
}

const { isOverDropZone } = useDropZone(topicPreviewDropZoneRef, (files) => {
  setPreviewFile(files?.[0] || null)
})

async function uploadTopicPreview(topicId: string, file: File) {
  const formData = new FormData()
  formData.append('topicId', topicId)
  formData.append('file', file)

  await $fetch<{ topic: TopicMeta }>('/api/topics/preview', {
    method: 'POST',
    body: formData
  })
}

async function createRootTopic() {
  if (!topicTitle.value.trim() || creatingTopic.value) {
    return
  }

  creatingTopic.value = true

  await $fetch<TopicMeta>('/api/topics', {
    method: 'POST',
    body: {
      title: topicTitle.value,
      name: topicName.value,
      description: topicDescription.value,
      hidden: topicHidden.value,
      parentId: null
    }
  }).then(async (created) => {
    if (topicPreviewFile.value) {
      await uploadTopicPreview(created.id, topicPreviewFile.value).catch((err) => {
        toast.add({
          color: 'neutral',
          title: 'Topic created without preview image',
          description: err.data?.message || err.message
        })
      })
    }

    await refresh()
    isCreateOpen.value = false
    topicTitle.value = ''
    topicName.value = ''
    topicDescription.value = ''
    topicHidden.value = false
    clearPreviewSelection()
    await router.push(`/topics/${created.name}`)
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to create topic',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    creatingTopic.value = false
  })
}

async function deleteRootTopic(topic: TopicMeta) {
  if (!loggedIn.value || !topic.canDelete) {
    return
  }

  if (!window.confirm(`Delete topic "${topic.title}"? This cannot be undone.`)) {
    return
  }

  await $fetch(`/api/topics/${topic.id}`, {
    method: 'DELETE'
  }).then(async () => {
    await refresh()
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to delete topic',
      description: err.data?.message || err.message
    })
  })
}
</script>

<template>
  <main class="relative isolate min-h-screen p-6 md:p-10 pt-28 md:pt-32 flex flex-col">
    <div
      v-if="topicBackgroundImagePath"
      class="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
    >
      <img
        :src="imageUrl(topicBackgroundImagePath, 'full')"
        alt=""
        class="h-full w-full object-cover"
      >
      <div class="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black/90" />
    </div>

    <div class="relative z-10 max-w-6xl mx-auto w-full flex-1 space-y-8">
      <div class="text-center space-y-3">
        <h1 class="text-3xl font-semibold text-white">
          Analog Anonymous
        </h1>
        <p class="text-gray-400 max-w-2xl mx-auto">
          Hi, my name is Loki, and I'm a barely functioning photographolic.
        </p>

        <div class="flex justify-center">
          <UModal
            v-if="loggedIn"
            v-model:open="isCreateOpen"
            title="Create Root Topic"
            :close="{
              color: 'gray',
              variant: 'outline',
              class: 'rounded-full'
            }"
          >
            <UButton
              label="New Topic"
              icon="i-heroicons-plus"
              color="primary"
            />
            <template #body>
              <form
                class="flex flex-col gap-4"
                @submit.prevent="createRootTopic"
              >
                <input
                  ref="topicPreviewInputRef"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="onPreviewFileChange"
                >
                <UInput
                  v-model="topicTitle"
                  placeholder="Title"
                  required
                />
                <UInput
                  v-model="topicName"
                  placeholder="Slug (auto-generated, editable)"
                />
                <UTextarea
                  v-model="topicDescription"
                  placeholder="Short description"
                  :rows="3"
                />
                <UCheckbox
                  v-model="topicHidden"
                  label="Hidden (not shown in root topic listing)"
                />
                <div class="space-y-2">
                  <p class="text-sm text-gray-300">
                    Topic preview image
                  </p>
                  <div
                    v-if="topicPreviewObjectUrl"
                    class="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-3"
                  >
                    <img
                      :src="topicPreviewObjectUrl"
                      alt="Selected topic preview"
                      class="h-14 w-14 rounded-md object-cover border border-white/10"
                    >
                    <div class="flex gap-2 ml-auto">
                      <UButton
                        type="button"
                        size="xs"
                        color="neutral"
                        variant="soft"
                        label="Replace"
                        @click="openPreviewPicker"
                      />
                      <UButton
                        type="button"
                        size="xs"
                        color="red"
                        variant="soft"
                        label="Remove"
                        @click="clearPreviewSelection"
                      />
                    </div>
                  </div>
                  <button
                    v-else
                    ref="topicPreviewDropZoneRef"
                    type="button"
                    class="w-full rounded-lg border border-dashed p-4 text-sm transition-colors text-left"
                    :class="isOverDropZone ? 'border-primary-400 bg-primary-500/10 text-white' : 'border-white/20 bg-black/20 text-gray-300 hover:border-white/40'"
                    @click="openPreviewPicker"
                  >
                    Click or drag & drop an image
                  </button>
                </div>
                <div class="flex justify-end">
                  <UButton
                    type="submit"
                    :loading="creatingTopic"
                    label="Create"
                    color="primary"
                  />
                </div>
              </form>
            </template>
          </UModal>
        </div>
      </div>

      <div
        v-if="visibleTopics.length"
        class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 max-w-6xl mx-auto"
      >
        <NuxtLink
          v-for="topic in visibleTopics"
          :key="topic.id"
          :to="`/topics/${topic.name}`"
          class="group relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-4 sm:p-5 hover:border-white/30 transition-colors min-h-48 sm:min-h-56 flex flex-col justify-end"
        >
          <UButton
            v-if="loggedIn && topic.canDelete"
            icon="i-heroicons-trash-20-solid"
            color="red"
            variant="soft"
            size="xs"
            class="absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Delete topic"
            @click.prevent.stop="deleteRootTopic(topic)"
          />
          <img
            v-if="topic.previewImagePath"
            :src="imageUrl(topic.previewImagePath, 'thumb')"
            :alt="topic.title"
            class="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-300"
          >
          <div
            v-if="topic.previewImagePath"
            class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20"
          />
          <div class="relative z-10 mt-auto space-y-2">
            <h2 class="text-white text-base sm:text-lg font-medium break-words leading-snug">
              {{ topic.title }}
            </h2>
            <p class="text-gray-200 line-clamp-3">
              {{ topic.description || '' }}
            </p>
          </div>
        </NuxtLink>
      </div>

      <div
        v-else
        class="text-center text-gray-400 py-20"
      >
        No topics yet.
      </div>
    </div>

    <AppCopyrightFooter />
  </main>
</template>
